/**
 * ID Verification modal — NEW surface, transcribed from export
 * 'Pre-screen (4).png' (not in the written spec): Document Type / Full
 * Name / Expiry Date header tiles, three labeled photo tiles (Biometric
 * Photo / Front of ID / Back of ID), centered Download footer.
 * Data: the v2 DTO's `veriffDocuments` refs (data-map §5). Document Type and
 * Expiry Date come from `idVerification` (derived from the Veriff record's
 * `verification.document` payload); each falls back to '-' when the payload
 * omits it. Images load straight from the blob URLs exactly like v1's
 * VeriffDocumentsModal, hiding themselves on error.
 */
import { ImageOff, Maximize } from 'lucide-react'
import {
	deriveVerificationTiles,
	findFacePhoto,
	isVerifiedStatus,
} from '#app/utils/application-detail-v2/display'
import type {
	ApplicationSummary,
	IdVerificationFacts,
	ParsedStepData,
	VeriffDocumentRef,
} from '#app/utils/application-detail-v2/types'
import {
	StatTile,
	VerificationModalFooter,
	VerificationModalShell,
} from './verification-bits'

const PHOTO_TILES = [
	{ name: 'face-pre', label: 'Biometric Photo' },
	{ name: 'document-front', label: 'Front of ID' },
	{ name: 'document-back', label: 'Back of ID' },
] as const

function PhotoTile({
	label,
	doc,
}: {
	label: string
	doc: VeriffDocumentRef | undefined
}) {
	return (
		<div className="flex flex-col items-center gap-3">
			<div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-[#F0F0EE] bg-[#F6F7F5]">
				{doc ? (
					<>
						<img
							src={doc.url}
							alt={label}
							className="size-full object-cover"
							onError={(event) => {
								event.currentTarget.style.display = 'none'
							}}
						/>
						<a
							href={doc.url}
							target="_blank"
							rel="noopener noreferrer"
							aria-label={`Open ${label} in a new tab`}
							className="absolute top-2 right-2 flex size-7 items-center justify-center rounded-lg bg-[#32302F] text-[#FFFFFF] transition-colors hover:bg-white hover:text-[#32302F]"
						>
							<Maximize className="size-3.5" />
						</a>
					</>
				) : (
					<div className="flex size-full flex-col items-center justify-center gap-2 text-gray-300">
						<ImageOff className="size-6" />
						<span className="text-xs">Not available</span>
					</div>
				)}
			</div>
			<span className="rounded-full bg-[#F1F2F0] px-4 py-1 text-xs font-medium text-[#32302F]">
				{label}
			</span>
		</div>
	)
}

export function IdVerificationModal({
	open,
	onOpenChange,
	application,
	veriffDocuments,
	idVerification,
	idVerificationStep,
	aiReportData,
	canDownload,
	onDownload,
	downloadBusy,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
	veriffDocuments: VeriffDocumentRef[]
	idVerification: IdVerificationFacts
	idVerificationStep: ParsedStepData['id-verification']
	aiReportData: Record<string, unknown> | null
	/** `can.download` (data.export) — gates the Download button. */
	canDownload: boolean
	onDownload: () => void
	downloadBusy: boolean
}) {
	// Verified when either the AI report marks the ID section verified or the
	// Veriff step itself reports approval (pre-screen has both sources).
	const veriffStatus =
		idVerificationStep?.verificationStatus ?? idVerificationStep?.status
	const verified =
		deriveVerificationTiles(aiReportData).id.verified ||
		isVerifiedStatus(veriffStatus) ||
		veriffStatus?.toLowerCase() === 'approved'

	// Face photo via the shared loose-match helper (same photo as the header
	// avatar); the front/back tiles match strictly by name.
	const docFor = (name: string): VeriffDocumentRef | undefined =>
		name === 'face-pre'
			? findFacePhoto(veriffDocuments)
			: veriffDocuments.find((doc) => doc.name === name)

	return (
		<VerificationModalShell
			open={open}
			onOpenChange={onOpenChange}
			title="ID Verification"
			verified={verified}
		>
			<div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
				<StatTile
					tone="purple"
					icon={<img src="/img/pageline.svg" alt="" />}
					label="Document Type"
					value={idVerification.documentType ?? '-'}
				/>
				<StatTile
					tone="rose"
					icon={<img src="/img/usercard.svg" alt="" />}
					label="Full Name"
					value={idVerification.documentName ?? '-'}
				/>
				<StatTile
					tone="dgreen"
					icon={<img src="/img/userx.svg" alt="" />}
					label="Expiry Date"
					value={idVerification.documentExpiry ?? '-'}
				/>
			</div>
			<div className="mt-5 rounded-xl border border-[#F0F0F0] bg-[#fbfdfd] p-4">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					{PHOTO_TILES.map((tile) => (
						<PhotoTile
							key={tile.name}
							label={tile.label}
							doc={docFor(tile.name)}
						/>
					))}
				</div>
			</div>

			{veriffDocuments.length > 0 && canDownload ? (
				<div className="flex justify-center">
					<VerificationModalFooter
						showRequestBank={false}
						onRequestBank={() => {}}
						showDownload
						onDownload={onDownload}
						downloadBusy={downloadBusy}
					/>
				</div>
			) : null}
		</VerificationModalShell>
	)
}
